import BasicModal from "@/modules/common/components/modals/basic-modal";
import ModalHeader from "@/modules/common/components/ui-kit/modals/modal-header";
import {
  Box,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { ChevronRightIcon } from "lucide-react";
import { useState } from "react";
import { ProjectTransaction } from "../../dtos/tenants";
import { formattedDate } from "../../utils/rental-info";
import CommonBottomSheet from "@/modules/common/components/ui-kit/bottom-sheets/common-bottom-sheet";
import useScreenSize from "@/modules/common/hooks/device-detection.hook";
import { TextButton } from "@/modules/common/components/ui/text-button";
import { formatCurrency } from "@/modules/common/utils";

const HeaderTableCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: theme.palette.grey[100],
  fontWeight: 600,
  borderRight: `1px solid ${theme.palette.divider}`,
}));
const CustomTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 400,
  borderRight: `1px solid ${theme.palette.divider}`,
}));

const PaymentPlanStatus = ({
  status,
}: {
  status:
    | "PendingApproval"
    | "PendingPayment"
    | "Paid"
    | "Failed"
    | "Cancelled";
}) => {
  const getStatusColors = (status: string) => {
    switch (status) {
      case "Failed":
      case "Cancelled":
        return {
          label: status,
          text: "#AB2018",
          bgcolor: "#FFC8C2",
        };
      case "Paid":
        return {
          label: status,
          text: "#326054",
          bgcolor: "#CFE8E2",
        };
      case "PendingPayment":
        return {
          label: "Pending Payment",
          text: "#5D5D13",
          bgcolor: "#F3F3C4",
        };
      case "PendingApproval":
        return {
          label: "Pending Approval",
          text: "#5D5D13",
          bgcolor: "#F3F3C4",
        };
      default:
        return {
          label: "Unknown",
          text: "#313131",
          bgcolor: "#E0E0E0",
        };
    }
  };

  const formattedStatus = getStatusColors(status);

  return (
    <Stack
      bgcolor={formattedStatus.bgcolor}
      borderRadius={(theme) => theme.nawy.radius.md}
      p={(theme) => theme.nawy.spacing.xs}
      width="fit-content"
    >
      <Typography fontSize={11} fontWeight={600} color={formattedStatus.text}>
        {formattedStatus.label}
      </Typography>
    </Stack>
  );
};

const PaymentPlansContent = ({
  paymentPlans,
}: Readonly<{ paymentPlans: ProjectTransaction[] }>) => (
  <Box p={1}>
    <TableContainer
      sx={{
        maxHeight: "70vh",
        borderRadius: 2,
        border: "1px solid #e0e0e0",
      }}
    >
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <HeaderTableCell>Date</HeaderTableCell>
            <HeaderTableCell>Amount</HeaderTableCell>
            <HeaderTableCell sx={{ borderRight: "none" }}>
              Status
            </HeaderTableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {paymentPlans?.map((pp) => (
            <TableRow key={pp?.id} hover>
              <CustomTableCell>{formattedDate(pp.date)}</CustomTableCell>
              <CustomTableCell>
                EGP {formatCurrency(pp.amount.toString())}
              </CustomTableCell>
              <CustomTableCell>
                <PaymentPlanStatus status={pp.status} />
              </CustomTableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  </Box>
);

function TenantsPaymentPlans({
  paymentPlans,
}: Readonly<{
  paymentPlans: ProjectTransaction[];
}>) {
  const { isMobile } = useScreenSize();
  const [open, setOpen] = useState(false);
  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      <TextButton
        onClick={() => setOpen(true)}
        sx={{
          width: "fit-content",
          textTransform: "capitalize",
          fontSize: 14,
          fontWeight: 600,
          p: 0,
        }}
        endIcon={<ChevronRightIcon size={16} />}
      >
        payment plans
      </TextButton>

      {isMobile ? (
        <CommonBottomSheet
          isOpen={open}
          onClose={handleClose}
          snapPoints={[0.8, 0.8]}
        >
          <Stack p={2} pt={0}>
            <Typography fontWeight={600} fontSize={16}>
              Payment plans
            </Typography>
          </Stack>
          <PaymentPlansContent paymentPlans={paymentPlans} />
        </CommonBottomSheet>
      ) : (
        <BasicModal
          width={480}
          open={open}
          setOpen={setOpen}
          onClose={handleClose}
        >
          <ModalHeader title="Payment plans" onClose={handleClose} />
          <PaymentPlansContent paymentPlans={paymentPlans} />
        </BasicModal>
      )}
    </>
  );
}

export default TenantsPaymentPlans;
